import { APIRequestSort } from 'global';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { InputField, SelectField } from 'components/form';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';

import { ActivityListFilters, ActivityPageTabs } from '../types';
import { callDirectionOptions, callStatusOptions, initialFilters } from '../constants';

interface Props {
  loading: boolean;
  type: ActivityPageTabs;
  onFiltersChanged: (nextFilters: ActivityListFilters) => void;
  onSortingChanged: (nextSorting: APIRequestSort<'created_date_time'>) => void;
}

export const DealActivityFilters = ({ type, loading, onFiltersChanged, onSortingChanged }: Props) => {
  const [sorting, setSorting] = useState<APIRequestSort<'created_date_time'>>({
    key: 'created_date_time',
    value: 'desc',
  });

  const form = useForm<{ filters: ActivityListFilters }>({
    defaultValues: { filters: initialFilters },
  });

  const filters = useWatch({ control: form.control, name: 'filters' });

  useEffect(() => {
    onFiltersChanged(filters);
  }, [filters, onFiltersChanged]);

  useEffect(() => {
    onSortingChanged(sorting);
  }, [sorting, onSortingChanged]);

  const handleResetFilters = () => {
    form.reset();
  };

  return (
    <Stack direction="row" alignItems="center" gap="0.5rem">
      <Tooltip
        title={sorting.value === 'desc' ? 'Sorted descending by created date' : 'Sorted ascending by created date'}
      >
        <IconButton
          color="primary"
          loading={loading}
          className="icon-btn-bordered"
          onClick={() =>
            setSorting(prev => ({
              ...prev,
              value: prev.value === 'asc' ? 'desc' : 'asc',
            }))
          }
        >
          {sorting.value === 'desc' ? <ArrowDownward color="primary" /> : <ArrowUpward color="primary" />}
        </IconButton>
      </Tooltip>

      <InputField label="Search" name="filters.search" control={form.control} inputProps={{ variant: 'outlined' }} />

      {type === ActivityPageTabs.CALL && (
        <SelectField
          label="Direction"
          variant="outlined"
          control={form.control}
          options={callDirectionOptions}
          name="filters.callFilters.callDirection"
        />
      )}

      {type === ActivityPageTabs.CALL && (
        <SelectField
          label="Status"
          variant="outlined"
          control={form.control}
          options={callStatusOptions}
          name="filters.callFilters.callStatus"
        />
      )}

      <Button disabled={loading} variant="contained" onClick={handleResetFilters}>
        Reset
      </Button>
    </Stack>
  );
};
